import { useState, useEffect, useCallback } from 'react';
import { purchaseReportingApi } from '../services/purchaseReportingApi';
import type { PRFilterOptions, PRFilters } from '../types/purchaseReporting';

const STORAGE_KEY = 'pr-filters';

function getYtdDefaults(): { dateFrom: string; dateTo: string } {
    const now = new Date();
    const year = now.getFullYear();
    const lastCompleteMonthEnd = new Date(year, now.getMonth(), 0);
    const m = String(lastCompleteMonthEnd.getMonth() + 1).padStart(2, '0');
    const d = String(lastCompleteMonthEnd.getDate()).padStart(2, '0');
    return {
        dateFrom: `${year}-01-01`,
        dateTo: `${year}-${m}-${d}`,
    };
}

function loadSavedFilters(): Partial<PRFilters> {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return {};
}

function saveFilters(filters: PRFilters) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            company: filters.company,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            businessUnit: filters.businessUnit,
            vendorID: filters.vendorID,
            locationID: filters.locationID,
        }));
    } catch { /* ignore */ }
}

export function usePurchaseReporting() {
    const ytd = getYtdDefaults();
    const saved = loadSavedFilters();

    const [filterOptions, setFilterOptions] = useState<PRFilterOptions>({
        companies: [],
        dateRange: { min: ytd.dateFrom, max: ytd.dateTo },
        businessUnits: [],
        vendors: [],
        warehouses: [],
    });
    const [filters, setFilters] = useState<PRFilters>({
        company: saved.company || '',
        dateFrom: saved.dateFrom || ytd.dateFrom,
        dateTo: saved.dateTo || ytd.dateTo,
        businessUnit: saved.businessUnit || '',
        vendorID: saved.vendorID || '',
        locationID: saved.locationID || '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        purchaseReportingApi.getFilters()
            .then(opts => setFilterOptions(opts))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        saveFilters(filters);
    }, [filters]);

    const updateFilters = useCallback((updates: Partial<PRFilters>) => {
        setFilters(prev => ({ ...prev, ...updates }));
    }, []);

    const resetFilters = useCallback(() => {
        const ytdDates = getYtdDefaults();
        setFilters({
            company: '',
            dateFrom: ytdDates.dateFrom,
            dateTo: ytdDates.dateTo,
            businessUnit: '',
            vendorID: '',
            locationID: '',
        });
    }, []);

    return {
        filterOptions,
        filters,
        updateFilters,
        resetFilters,
        loading,
        error,
    };
}
